import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Mail, UserPlus, ChevronDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input, Select, Checkbox } from '../ui/form';
import { Badge } from '../ui/badge';
import { Card } from '../layout';
import { WorkflowStepSummaryDto, WorkflowStepDetailDto, CreateWorkflowStepDto, WorkflowStepApproversDto, WorkflowStepConditionsDto, DivisionRole, UserDto } from '../../types';
import { useDivisionRoles } from '../../hooks/use-division-roles';
import { useDivisionUsers } from '../../hooks/use-division-users';
import { getStepTypeLabel } from '../../utils';

interface WorkflowStepsEditorProps {
  divisionId: string;
  workflowSteps: (WorkflowStepSummaryDto | WorkflowStepDetailDto)[];
  onChange: (workflowSteps: CreateWorkflowStepDto[]) => void;
  /** When true, a workflow- or division-level setting already forces "Require explicit Submit" on for every step - the per-step checkbox is shown checked and disabled. */
  forceRequireExplicitSubmit?: boolean;
}

interface EditingWorkflowStep extends CreateWorkflowStepDto {
  id?: string; // For tracking purposes - includes both temporary and real IDs
  hasExistingId?: boolean; // True if this step has a real ID from the API
  level?: number; // Internal tracking for expand/collapse state (distinct from step number)
}

export function WorkflowStepsEditor({ divisionId, workflowSteps, onChange, forceRequireExplicitSubmit }: WorkflowStepsEditorProps) {
  const [editingSteps, setEditingSteps] = useState<EditingWorkflowStep[]>([]);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchingStepIndex, setSearchingStepIndex] = useState<number | null>(null);
  const [newEmail, setNewEmail] = useState('');
  // Guards the initialization effect against re-running when the workflowSteps
  // prop change was itself caused by our own onChange echo (see emitChange).
  const isInternalUpdate = useRef(false);

  // Fetch division roles for the dropdown
  const { data: divisionRolesResponse, isLoading: isLoadingRoles } = useDivisionRoles(divisionId);
  const divisionRoles = divisionRolesResponse?.roles || [];

  // Fetch all division users (for displaying selected users by email)
  const { data: allUsersResponse } = useDivisionUsers(
    divisionId,
    {
      active: true,
      limit: 1000
    },
    true // Always fetch to display selected users
  );

  const allUsers = allUsersResponse?.users || [];

  // Fetch users from API with search filter (only when searching)
  const { data: searchUsersResponse, isLoading: isLoadingUsers } = useDivisionUsers(
    divisionId,
    {
      search: userSearchQuery,
      active: true,
      limit: 50
    },
    userSearchQuery.length >= 2 && searchingStepIndex !== null
  );

  const searchResults = searchUsersResponse?.users || [];

  // Initialize editing steps from props whenever workflowSteps change
  // BUT only if it's not an internal update (from our own onChange call)
  useEffect(() => {
    // If this is an internal update, skip re-initialization to prevent loop
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const initialSteps: EditingWorkflowStep[] = workflowSteps.map((step, index) => {
      // Check if this step has a real ID from the API
      const hasRealId = 'id' in step && step.id && step.id.trim() !== '';
      
      return {
        step: step.step, // API returns 'step' property
        level: step.step, // Internal tracking for expand/collapse
        name: step.name,
        description: step.description || '',
        stepType: step.stepType || 'approval',
        canEditQuantity: step.canEditQuantity ?? false,
        allowQuantityIncrease: step.allowQuantityIncrease ?? false,
        orderLevelOnly: step.orderLevelOnly ?? false,
        requireSignature: step.requireSignature ?? false,
        requireExplicitSubmit: step.requireExplicitSubmit ?? false,
        approvers: {
          // Preserve the exact type from the API - don't default it
          type: step.approvers.type,
          roles: step.approvers.roles || [],
          userIds: step.approvers.userIds || [],
          divisionRoleIds: step.approvers.divisionRoleIds || [],
          matchIdentifiers: step.approvers.matchIdentifiers || [],
          inviteDivisionRoleId: step.approvers.inviteDivisionRoleId,
          parallelApproval: step.approvers.parallelApproval ?? true,
          requiredApprovals: step.approvers.requiredApprovals || 1,
          timeoutHours: step.approvers.timeoutHours,
          timeoutWarningHours: step.approvers.timeoutWarningHours
        },
        conditions: {
          amountThreshold: step.conditions.amountThreshold,
          skipIfApprovedBy: step.conditions.skipIfApprovedBy || [],
          bypassIfNoApprover: step.conditions.bypassIfNoApprover ?? false,
          auditBypass: step.conditions.auditBypass ?? false
        },
        id: hasRealId ? step.id! : `step-${index}`,
        hasExistingId: Boolean(hasRealId)
      };
    });
    setEditingSteps(initialSteps);
  }, [workflowSteps]); // Re-initialize whenever workflowSteps prop changes

  // Notify the parent of a change. Called directly from each mutation handler
  // (rather than from a useEffect keyed on editingSteps) so that it only ever
  // fires in response to an explicit user action - never as a side effect of
  // the initialization effect above picking up new data from props. Relying
  // on a useEffect here previously caused a race under React StrictMode
  // (which double-invokes effects): the echo effect could fire with the
  // still-empty initial state and clobber the real steps moments after they
  // arrived from the API, making the editor appear to have no steps.
  const emitChange = (steps: EditingWorkflowStep[]) => {
    const cleanedSteps = steps.map(({ id, hasExistingId, level, ...step }) => ({
      ...step,
      approvers: {
        ...step.approvers,
        // Ensure divisionRoleIds is always present as an array
        divisionRoleIds: step.approvers.divisionRoleIds || [],
      }
    }));

    // Mark that we're making an internal update so the initialization effect skips
    isInternalUpdate.current = true;
    onChange(cleanedSteps);
  };

  const addNewStep = () => {
    const newStep: EditingWorkflowStep = {
      step: editingSteps.length + 1,
      level: editingSteps.length + 1, // For expand/collapse tracking
      name: `Step ${editingSteps.length + 1}`,
      description: '',
      stepType: 'approval',
      canEditQuantity: false,
      allowQuantityIncrease: false,
      orderLevelOnly: false,
      requireSignature: false,
      requireExplicitSubmit: false,
      approvers: {
        type: 'division_role',
        roles: [],
        userIds: [],
        divisionRoleIds: [],
        matchIdentifiers: [],
        inviteEmails: [],
        parallelApproval: true,
        requiredApprovals: 1,
        timeoutHours: 48,
        timeoutWarningHours: undefined
      },
      conditions: {
        amountThreshold: undefined,
        skipIfApprovedBy: [],
        bypassIfNoApprover: false,
        auditBypass: false
      },
      id: `step-${Date.now()}`
    };
    const newSteps = [...editingSteps, newStep];
    setEditingSteps(newSteps);
    emitChange(newSteps);
    setExpandedStep(newStep.step); // Use step number for expand/collapse tracking
  };

  const removeStep = (stepIndex: number) => {
    const newSteps = editingSteps
      .filter((_, index) => index !== stepIndex)
      .map((step, index) => ({ ...step, step: index + 1, level: index + 1 })); // Reorder steps
    setEditingSteps(newSteps);
    emitChange(newSteps);
  };

  const updateStep = (stepIndex: number, field: keyof EditingWorkflowStep, value: any) => {
    const newSteps = editingSteps.map((step, index) => 
      index === stepIndex ? { ...step, [field]: value } : step
    );
    setEditingSteps(newSteps);
    emitChange(newSteps);
  };

  const updateApprovers = (stepIndex: number, field: keyof WorkflowStepApproversDto, value: any) => {
    const newSteps = editingSteps.map((step, index) => 
      index === stepIndex 
        ? { ...step, approvers: { ...step.approvers, [field]: value } }
        : step
    );
    setEditingSteps(newSteps);
    emitChange(newSteps);
  };

  const updateConditions = (stepIndex: number, field: keyof WorkflowStepConditionsDto, value: any) => {
    const newSteps = editingSteps.map((step, index) => 
      index === stepIndex 
        ? { ...step, conditions: { ...step.conditions, [field]: value } }
        : step
    );
    setEditingSteps(newSteps);
    emitChange(newSteps);
  };

  const moveStep = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= editingSteps.length) return;

    const newSteps = [...editingSteps];
    [newSteps[fromIndex], newSteps[toIndex]] = [newSteps[toIndex], newSteps[fromIndex]];
    
    // Update step numbers
    newSteps.forEach((step, index) => {
      step.step = index + 1;
      step.level = index + 1;
    });
    
    setEditingSteps(newSteps);
    emitChange(newSteps);
  };

  const addSkipRole = (stepIndex: number, role: string) => {
    if (!role.trim()) return;
    const step = editingSteps[stepIndex];
    const currentRoles = step.conditions.skipIfApprovedBy || [];
    if (!currentRoles.includes(role)) {
      updateConditions(stepIndex, 'skipIfApprovedBy', [...currentRoles, role]);
    }
  };

  const removeSkipRole = (stepIndex: number, role: string) => {
    const step = editingSteps[stepIndex];
    const currentRoles = step.conditions.skipIfApprovedBy || [];
    updateConditions(stepIndex, 'skipIfApprovedBy', currentRoles.filter(r => r !== role));
  };

  const addDivisionRole = (stepIndex: number, roleId: string) => {
    if (!roleId.trim()) return;
    const step = editingSteps[stepIndex];
    const currentRoleIds = step.approvers.divisionRoleIds || [];
    if (!currentRoleIds.includes(roleId)) {
      updateApprovers(stepIndex, 'divisionRoleIds', [...currentRoleIds, roleId]);
    }
  };

  const removeDivisionRole = (stepIndex: number, roleId: string) => {
    const step = editingSteps[stepIndex];
    const currentRoleIds = step.approvers.divisionRoleIds || [];
    updateApprovers(stepIndex, 'divisionRoleIds', currentRoleIds.filter(id => id !== roleId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Workflow Steps</h3>
        <Button type="button" variant="outline" size="sm" onClick={addNewStep}>
          Add Step
        </Button>
      </div>

      {editingSteps.length === 0 && (
        <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
          No workflow steps configured. Click "Add Step" to create your first workflow step.
        </div>
      )}

      {editingSteps.map((step, index) => {
        const isExpanded = expandedStep === step.step;
        const isNotification = step.stepType === 'notification';
        const isConfirmation = step.stepType === 'confirmation';
        const approverCount = step.approvers.type === 'user_specific'
          ? (step.approvers.userIds?.length || 0) + (step.approvers.inviteEmails?.length || 0)
          : (step.approvers.divisionRoleIds?.length || 0) + (step.approvers.roles?.length || 0);
        const summaryParts = [
          !isNotification ? `${approverCount} approver${approverCount === 1 ? '' : 's'}` : null,
          !isNotification && step.approvers.requiredApprovals ? `${step.approvers.requiredApprovals} required` : null,
          step.approvers.timeoutHours ? `${step.approvers.timeoutHours}h timeout` : null,
        ].filter(Boolean) as string[];

        return (
        <Card key={step.id} padding={false} className="overflow-hidden">
          {/* Step Header - click anywhere to expand/collapse; controls below stop propagation */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setExpandedStep(isExpanded ? null : step.step)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpandedStep(isExpanded ? null : step.step);
              }
            }}
            className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-secondary-700"
          >
            <div className="flex items-center gap-2 min-w-0">
              <ChevronDown
                className={`w-4 h-4 text-gray-400 dark:text-secondary-500 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
              />
              <Badge variant="info">Step {step.step}</Badge>
              <Badge variant="default">{getStepTypeLabel(step.stepType)}</Badge>
              <span className="font-medium text-gray-900 dark:text-white truncate">{step.name}</span>
              {!isExpanded && summaryParts.length > 0 && (
                <span className="hidden sm:inline text-sm text-gray-500 dark:text-secondary-400 truncate">
                  · {summaryParts.join(' · ')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => moveStep(index, 'up')}
                disabled={index === 0}
                aria-label="Move step up"
                title="Move step up"
              >
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => moveStep(index, 'down')}
                disabled={index === editingSteps.length - 1}
                aria-label="Move step down"
                title="Move step down"
              >
                <ArrowDown className="w-4 h-4" />
              </Button>
              <div className="w-px h-5 bg-gray-200 dark:bg-secondary-700 mx-1" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm(`Remove "${step.name}"? This cannot be undone.`)) {
                    removeStep(index);
                  }
                }}
                className="text-red-600 dark:text-red-400 hover:text-red-700 hover:border-red-300"
                aria-label="Remove step"
                title="Remove step"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Expanded step details */}
          {isExpanded && (
              <div className="space-y-4 border-t border-gray-100 dark:border-secondary-700 px-4 py-4">
                {/* Basic Information */}
                <div className="rounded-lg border border-gray-100 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900/50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                        Step Name
                      </label>
                      <Input
                        type="text"
                        value={step.name}
                        onChange={(e) => updateStep(index, 'name', e.target.value)}
                        placeholder="e.g., Manager Approval"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                        Step Type
                      </label>
                      <Select
                        value={step.stepType || 'approval'}
                        onChange={(e) => updateStep(index, 'stepType', e.target.value)}
                      >
                        <option value="approval">Approval</option>
                        <option value="fulfillment">Fulfillment</option>
                        <option value="notification">Notification</option>
                        <option value="review">Review</option>
                        <option value="confirmation">Confirm Pickup</option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                        Description
                      </label>
                      <Input
                        type="text"
                        value={step.description}
                        onChange={(e) => updateStep(index, 'description', e.target.value)}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 border-t border-gray-100 dark:border-secondary-700 pt-3">
                    <label className={`flex items-center gap-2 ${isNotification ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={step.canEditQuantity ?? false}
                        onChange={(e) => updateStep(index, 'canEditQuantity', e.target.checked)}
                        disabled={isNotification}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Approver can edit quantities at this step
                      </span>
                    </label>
                    {!isNotification && (
                      <label className={`flex items-center gap-2 ml-6 ${step.canEditQuantity ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <input
                          type="checkbox"
                          checked={step.allowQuantityIncrease ?? false}
                          onChange={(e) => updateStep(index, 'allowQuantityIncrease', e.target.checked)}
                          disabled={!step.canEditQuantity}
                          className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                        <span className="text-sm text-gray-700 dark:text-secondary-200">
                          Allow increasing quantities (uncheck to only allow decreasing)
                        </span>
                      </label>
                    )}
                    <label className={`flex items-center gap-2 ${isNotification ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={step.orderLevelOnly ?? false}
                        onChange={(e) => updateStep(index, 'orderLevelOnly', e.target.checked)}
                        disabled={isNotification}
                        className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                        Order-level approval only (approvers approve or reject the entire order, not individual line items)
                      </span>
                    </label>
                    {isConfirmation && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.requireSignature ?? false}
                          onChange={(e) => updateStep(index, 'requireSignature', e.target.checked)}
                          className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                          Require a signature to confirm pickup (opens a signature capture popup)
                        </span>
                      </label>
                    )}
                    {!isNotification && (
                      <label className={`flex items-center gap-2 ${forceRequireExplicitSubmit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={forceRequireExplicitSubmit || (step.requireExplicitSubmit ?? false)}
                          onChange={(e) => updateStep(index, 'requireExplicitSubmit', e.target.checked)}
                          disabled={forceRequireExplicitSubmit}
                          className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 disabled:cursor-not-allowed bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                          Require explicit "Submit" (approvers draft decisions with Undo/Discard before committing)
                          {forceRequireExplicitSubmit && <span className="italic text-gray-500 dark:text-secondary-400"> - enforced by workflow/division setting</span>}
                        </span>
                      </label>
                    )}
                  </div>
                </div>

                {/* Approvers Configuration */}
                <div className="rounded-lg border border-gray-100 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900/50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Approvers</h4>
                  <div className="space-y-4">
                    {/* Approver Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                        Approver Type
                      </label>
                      <Select
                        value={step.approvers.type}
                        onChange={(e) => updateApprovers(index, 'type', e.target.value)}
                      >
                        <option value="division_role">Division Role Based</option>
                        <option value="role_based">Legacy Role Based (Deprecated)</option>
                        <option value="user_specific">User Specific</option>
                      </Select>
                    </div>

                    {/* Division Role-based Approvers */}
                    {(step.approvers.type === 'division_role' || step.approvers.type === 'role_based') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                          Division Roles
                        </label>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {(step.approvers.divisionRoleIds || []).map((roleId) => {
                              const role = divisionRoles.find(r => r.id === roleId);
                              return (
                                <Badge
                                  key={roleId}
                                  variant="default"
                                  className="flex items-center gap-1"
                                >
                                  {role?.name || roleId}
                                  <button
                                    type="button"
                                    onClick={() => removeDivisionRole(index, roleId)}
                                    className="ml-1 text-red-500 hover:text-red-700"
                                  >
                                    ×
                                  </button>
                                </Badge>
                              );
                            })}
                            {(step.approvers.divisionRoleIds || []).length === 0 && (
                              <div className="text-sm text-gray-500 dark:text-secondary-400 italic">
                                No division roles selected. Please select at least one role for approval.
                              </div>
                            )}
                          </div>
                          
                          {/* Division Role Selector */}
                          <div className="flex gap-2">
                            <Select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  addDivisionRole(index, e.target.value);
                                  // Note: Don't try to reset e.target.value directly
                                  // React will handle this through re-rendering with value=""
                                }
                              }}
                              disabled={isLoadingRoles}
                            >
                              <option value="">
                                {isLoadingRoles ? 'Loading roles...' : 'Select a division role...'}
                              </option>
                              {divisionRoles
                                .filter(role => !(step.approvers.divisionRoleIds || []).includes(role.id))
                                .map(role => (
                                  <option key={role.id} value={role.id}>
                                    {role.name}
                                  </option>
                                ))}
                            </Select>
                          </div>
                          
                          {/* Legacy Role Input - for backward compatibility */}
                          {(step.approvers.roles || []).length > 0 && (
                            <div className="border-t pt-3 mt-3">
                              <div className="text-sm text-amber-600 dark:text-amber-400 mb-2">
                                Legacy roles (consider migrating to division roles):
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(step.approvers.roles || []).map((role, roleIndex) => (
                                  <Badge
                                    key={roleIndex}
                                    variant="warning"
                                    className="flex items-center gap-1"
                                  >
                                    {role}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newRoles = step.approvers.roles?.filter((_, i) => i !== roleIndex) || [];
                                        updateApprovers(index, 'roles', newRoles);
                                      }}
                                      className="ml-1 text-red-500 hover:text-red-700"
                                    >
                                      ×
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* User-specific Approvers */}
                    {step.approvers.type === 'user_specific' && (
                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                          Assigned Users
                        </label>

                        {/* Search Existing Users */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-secondary-400 mb-2">
                            Search Existing Users
                          </label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-secondary-500 w-4 h-4" />
                            <input
                              type="text"
                              value={searchingStepIndex === index ? userSearchQuery : ''}
                              onChange={(e) => {
                                setUserSearchQuery(e.target.value);
                                setSearchingStepIndex(index);
                              }}
                              onFocus={() => setSearchingStepIndex(index)}
                              placeholder="Search by name or email..."
                              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                          </div>
                        </div>

                        {/* Available Users List */}
                        {searchingStepIndex === index && userSearchQuery && (
                          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-secondary-700 rounded-lg">
                            {isLoadingUsers ? (
                              <div className="p-4 text-center text-sm text-gray-500 dark:text-secondary-400">
                                Loading users...
                              </div>
                            ) : searchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-gray-500 dark:text-secondary-400">
                                No users found
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200 dark:divide-secondary-700">
                                {searchResults.map(user => {
                                  const isSelected = (step.approvers.userIds || []).includes(user.id);
                                  return (
                                    <div
                                      key={user.id}
                                      className="p-3 hover:bg-gray-50 dark:hover:bg-secondary-700 flex items-center justify-between"
                                    >
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                          {user.firstName} {user.lastName}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-secondary-400">{user.email}</p>
                                        {user.divisionRoles && user.divisionRoles.length > 0 && (
                                          <Badge className="mt-1 text-xs">
                                            {user.divisionRoles[0].role}
                                          </Badge>
                                        )}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant={isSelected ? 'outline' : 'primary'}
                                        onClick={() => {
                                          const newUserIds = isSelected
                                            ? (step.approvers.userIds || []).filter(id => id !== user.id)
                                            : [...(step.approvers.userIds || []), user.id];
                                          updateApprovers(index, 'userIds', newUserIds);
                                          if (!isSelected) {
                                            setUserSearchQuery('');
                                          }
                                        }}
                                      >
                                        {isSelected ? 'Remove' : 'Add'}
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Selected Users Display */}
                        {(step.approvers.userIds || []).length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-secondary-400 mb-2">
                              Selected Users ({(step.approvers.userIds || []).length})
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {(step.approvers.userIds || []).map((userId, userIndex) => {
                                // Try to find the user in all users list
                                const user = allUsers.find(u => u.id === userId);
                                const displayText = user ? user.email : userId;
                                
                                return (
                                  <div
                                    key={userIndex}
                                    className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950 text-blue-700 px-3 py-1.5 rounded-full text-sm"
                                  >
                                    <span>{displayText}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newUserIds = (step.approvers.userIds || []).filter((_, i) => i !== userIndex);
                                        updateApprovers(index, 'userIds', newUserIds);
                                      }}
                                      className="text-blue-500 hover:text-blue-700"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Invite New Users by Email (Optional Enhancement) */}
                        <div className="border-t border-gray-200 dark:border-secondary-700 pt-4">
                          <label className="block text-xs font-medium text-gray-600 dark:text-secondary-400 mb-2">
                            Or Invite by Email
                          </label>

                          <div className="mb-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-secondary-400 mb-1">
                              Division Role for Invited Approvers
                            </label>
                            <Select
                              value={step.approvers.inviteDivisionRoleId || ''}
                              onChange={(e) => updateApprovers(index, 'inviteDivisionRoleId', e.target.value || undefined)}
                            >
                              <option value="">Select a role...</option>
                              {divisionRoles.map((role: DivisionRole) => (
                                <option key={role.id} value={role.id}>{role.name}</option>
                              ))}
                            </Select>
                            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                              Invited users are assigned this role in the division so they can access it and appear in reports.
                            </p>
                          </div>

                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-secondary-500 w-4 h-4" />
                              <input
                                type="email"
                                value={searchingStepIndex === index ? newEmail : ''}
                                onChange={(e) => {
                                  setNewEmail(e.target.value);
                                  setSearchingStepIndex(index);
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && newEmail.trim()) {
                                    e.preventDefault();
                                    if (!step.approvers.inviteDivisionRoleId) {
                                      alert('Please select a division role before inviting users');
                                      return;
                                    }
                                    const trimmedEmail = newEmail.trim();
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (emailRegex.test(trimmedEmail)) {
                                      // Add to inviteEmails for backend to process as new user invitation
                                      const newInviteEmails = [...(step.approvers.inviteEmails || []), trimmedEmail];
                                      updateApprovers(index, 'inviteEmails', newInviteEmails);
                                      setNewEmail('');
                                    } else {
                                      alert('Please enter a valid email address');
                                    }
                                  }
                                }}
                                placeholder="Enter email address..."
                                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                              />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => {
                                if (!step.approvers.inviteDivisionRoleId) {
                                  alert('Please select a division role before inviting users');
                                  return;
                                }
                                const trimmedEmail = newEmail.trim();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                if (trimmedEmail && emailRegex.test(trimmedEmail)) {
                                  const newInviteEmails = [...(step.approvers.inviteEmails || []), trimmedEmail];
                                  updateApprovers(index, 'inviteEmails', newInviteEmails);
                                  setNewEmail('');
                                } else if (trimmedEmail) {
                                  alert('Please enter a valid email address');
                                }
                              }}
                              disabled={!newEmail.trim() || !step.approvers.inviteDivisionRoleId}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                            Users will be created in Okta and invited automatically
                          </p>

                          {/* Invited Emails Display */}
                          {(step.approvers.inviteEmails || []).length > 0 && (
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-gray-600 dark:text-secondary-400 mb-2">
                                Invited Emails ({(step.approvers.inviteEmails || []).length})
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {(step.approvers.inviteEmails || []).map((email, emailIndex) => (
                                  <div
                                    key={emailIndex}
                                    className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 px-3 py-1.5 rounded-full text-sm"
                                  >
                                    <Mail className="w-3 h-3" />
                                    <span>{email}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newInviteEmails = (step.approvers.inviteEmails || []).filter((_, i) => i !== emailIndex);
                                        updateApprovers(index, 'inviteEmails', newInviteEmails);
                                      }}
                                      className="text-green-500 hover:text-green-700"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Approval Behavior */}
                    <div className="border-t border-gray-100 dark:border-secondary-700 pt-4">
                      <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-secondary-400 mb-3">
                        Approval Behavior
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={step.stepType === 'notification' ? 'opacity-50' : ''}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                            Required Approvals
                          </label>
                          <Input
                            type="number"
                            min="1"
                            value={step.approvers.requiredApprovals}
                            onChange={(e) => updateApprovers(index, 'requiredApprovals', parseInt(e.target.value) || 1)}
                            disabled={step.stepType === 'notification'}
                          />
                        </div>
                        <div className={step.stepType === 'notification' ? 'opacity-50' : ''}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                            Timeout Hours
                          </label>
                          <Input
                            type="number"
                            min="1"
                            value={step.approvers.timeoutHours || ''}
                            onChange={(e) => updateApprovers(index, 'timeoutHours', e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="Optional"
                            disabled={step.stepType === 'notification'}
                          />
                        </div>
                        <div className={step.stepType === 'notification' ? 'opacity-50' : ''}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                            Timeout Warning (hours before)
                          </label>
                          <Input
                            type="number"
                            min="1"
                            value={step.approvers.timeoutWarningHours || ''}
                            onChange={(e) => updateApprovers(index, 'timeoutWarningHours', e.target.value ? parseInt(e.target.value) : undefined)}
                            placeholder="Optional"
                            disabled={step.stepType === 'notification'}
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-secondary-400">Send approvers a warning this many hours before the step times out</p>
                        </div>
                      </div>
                      <label className="mt-4 flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={step.approvers.parallelApproval}
                          onChange={(e) => updateApprovers(index, 'parallelApproval', e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                          Parallel Execution
                        </span>
                        <span className="text-xs text-gray-500 dark:text-secondary-400">
                          (approvers can act at the same time instead of in sequence)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Conditions */}
                <div className="rounded-lg border border-gray-100 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900/50 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Conditions</h4>
                  <div className="space-y-4">
                    {/* Amount Threshold */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                        Amount Threshold (optional)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={step.conditions.amountThreshold || ''}
                        onChange={(e) => updateConditions(index, 'amountThreshold', e.target.value ? parseFloat(e.target.value) : undefined)}
                        placeholder="Minimum amount to trigger this level"
                      />
                    </div>

                    {/* Skip If Approved By */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                        Skip If Approved By
                      </label>
                      <div className="space-y-2">
                        {/* Selected Skip Roles */}
                        <div className="flex flex-wrap gap-2">
                          {(step.conditions.skipIfApprovedBy || []).map((role) => (
                            <Badge
                              key={role}
                              variant="default"
                              className="flex items-center gap-1"
                            >
                              {role}
                              <button
                                type="button"
                                onClick={() => removeSkipRole(index, role)}
                                className="ml-1 text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                        {/* Add Skip Role */}
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="Role name (e.g., Admin, DivisionAdmin)"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const input = e.target as HTMLInputElement;
                                addSkipRole(index, input.value);
                                input.value = '';
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              const input = (e.target as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                              if (input) {
                                addSkipRole(index, input.value);
                                input.value = '';
                              }
                            }}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Bypass If No Approver */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.conditions.bypassIfNoApprover ?? false}
                          onChange={(e) => updateConditions(index, 'bypassIfNoApprover', e.target.checked)}
                          className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                          Bypass step if no approver is assigned
                        </span>
                      </label>
                      {step.conditions.bypassIfNoApprover && (
                        <label className="flex items-center gap-2 cursor-pointer ml-6">
                          <input
                            type="checkbox"
                            checked={step.conditions.auditBypass ?? false}
                            onChange={(e) => updateConditions(index, 'auditBypass', e.target.checked)}
                            className="rounded border-gray-300 dark:border-secondary-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                            Record bypass in audit log
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
        </Card>
        );
      })}
    </div>
  );
}
